import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowDown, Zap, Mountain, Anchor, Factory, Wheat, Server, Coins, Landmark, CircleDot, ChevronRight, Globe2, FileText } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BriefingRequestModal from '../components/BriefingRequestModal';
import { strategy } from '../data/strategy';
import { MotionReveal } from '../components/strategy/Motion';
import BridgeAnimation from '../components/strategy/BridgeAnimation';
import { CapitalBars, RoadmapArea } from '../components/strategy/Charts';

const PLATFORM_ICONS = [Zap, Mountain, Anchor, Factory, Wheat, Server, Coins, Landmark];

const Eyebrow = ({ children }) => (
  <div className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.2em] uppercase text-[#2f9e8e]">
    <span className="w-8 h-px bg-[#2f9e8e]/60" /> {children}
  </div>
);

const StrategyHome = () => {
  const [audience, setAudience] = useState(null);

  const openBriefing = (a) => setAudience(a);
  const closeBriefing = () => setAudience(null);

  return (
    <div className="App font-sans bg-white text-[#1a365d] min-h-screen">
      <Header onPartnerClick={() => openBriefing('capital')} />

      {/* HERO */}
      <section className="relative pt-32 pb-24 px-6 overflow-hidden bg-gradient-to-br from-white via-[#f6f9fb] to-white">
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: 'linear-gradient(#2f9e8e 1px, transparent 1px), linear-gradient(90deg, #2f9e8e 1px, transparent 1px)',
          backgroundSize: '80px 80px'
        }} />
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full bg-[#2f9e8e]/12 blur-3xl pointer-events-none" style={{ animation: 'big-pulse 8s ease-in-out infinite' }} />
        <div className="absolute -bottom-32 -left-32 w-[600px] h-[600px] rounded-full bg-[#c19a3c]/12 blur-3xl pointer-events-none" style={{ animation: 'big-pulse 10s ease-in-out infinite 2s' }} />

        <div className="relative max-w-7xl mx-auto">
          <Eyebrow>{strategy.hero.eyebrow}</Eyebrow>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl font-bold leading-[1.02] tracking-tight mt-6 mb-8 max-w-5xl text-[#1a365d]">
            The <span className="text-[#2f9e8e]">$500 Billion</span><br />
            Bet on Africa.
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 max-w-3xl leading-relaxed mb-6">
            {strategy.hero.sub}
          </p>
          <p className="text-base text-gray-500 max-w-3xl leading-relaxed mb-12">
            {strategy.hero.body}
          </p>

          <div className="flex flex-wrap gap-3 mb-16">
            <button onClick={() => openBriefing('capital')}
              className="group inline-flex items-center gap-2 bg-[#2f9e8e] hover:bg-[#268578] text-white px-7 py-4 rounded-lg font-semibold transition-all hover:shadow-xl hover:-translate-y-0.5"
              data-testid="home-cta-capital">
              {strategy.hero.primary} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button onClick={() => openBriefing('sovereign')}
              className="group inline-flex items-center gap-2 bg-[#c19a3c] hover:bg-[#a8842f] text-white px-7 py-4 rounded-lg font-semibold transition-all hover:shadow-xl hover:-translate-y-0.5"
              data-testid="home-cta-sovereign">
              {strategy.hero.secondary} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button onClick={() => openBriefing('corporate')}
              className="group inline-flex items-center gap-2 bg-white hover:bg-gray-50 border border-gray-300 hover:border-[#1a365d] text-[#1a365d] px-7 py-4 rounded-lg font-semibold transition-all"
              data-testid="home-cta-corporate">
              {strategy.hero.tertiary} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          {/* Headline stats grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-gray-200 rounded-lg overflow-hidden border border-gray-200">
            {strategy.headlineStats.map((s, i) => (
              <MotionReveal key={s.label} delay={i * 80} className="bg-white p-5">
                <div className="text-2xl md:text-3xl font-bold text-[#1a365d]">{s.value}</div>
                <div className="text-xs text-gray-600 mt-1 leading-tight">{s.label}</div>
                <div className="text-[10px] text-gray-400 mt-0.5">{s.sub}</div>
              </MotionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* THESIS */}
      <section className="relative py-24 px-6 bg-[#f6f9fb]">
        <div className="max-w-5xl mx-auto">
          <Eyebrow>The Core Insight</Eyebrow>
          <h2 className="font-serif text-3xl md:text-5xl font-bold leading-tight mt-5 mb-8 text-[#1a365d]">
            {strategy.thesis.title}
          </h2>
          {strategy.thesis.body.map((p, i) => (
            <p key={i} className="text-lg text-gray-700 leading-relaxed mb-5 max-w-4xl">{p}</p>
          ))}
        </div>
      </section>

      {/* AMERICA-AFRICA BRIDGE */}
      <section className="relative py-24 px-6 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-14">
            <Eyebrow>The America–Africa Bridge</Eyebrow>
            <h2 className="font-serif text-3xl md:text-5xl font-bold leading-tight mt-5 text-[#1a365d]">
              {strategy.bridge.title}
            </h2>
          </div>

          <MotionReveal className="mb-12 max-w-5xl mx-auto">
            <BridgeAnimation light />
          </MotionReveal>

          <div className="grid md:grid-cols-2 gap-px bg-gray-200 rounded-xl overflow-hidden border border-gray-200">
            <div className="bg-white p-8 md:p-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-[#e8f5f3] flex items-center justify-center"><Globe2 className="w-5 h-5 text-[#2f9e8e]" /></div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#2f9e8e] font-bold">{strategy.bridge.africa.label}</div>
              </div>
              <ul className="space-y-2.5">
                {strategy.bridge.africa.items.map((i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-700">
                    <CircleDot className="w-3.5 h-3.5 text-[#2f9e8e] flex-shrink-0" /> {i}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white p-8 md:p-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-[#fcf5e3] flex items-center justify-center"><Globe2 className="w-5 h-5 text-[#c19a3c]" /></div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#c19a3c] font-bold">{strategy.bridge.usa.label}</div>
              </div>
              <ul className="space-y-2.5">
                {strategy.bridge.usa.items.map((i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-700">
                    <CircleDot className="w-3.5 h-3.5 text-[#c19a3c] flex-shrink-0" /> {i}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FLYWHEEL */}
      <section className="py-24 px-6 bg-[#f6f9fb]">
        <div className="max-w-5xl mx-auto">
          <Eyebrow>The BIG Flywheel</Eyebrow>
          <h2 className="font-serif text-3xl md:text-5xl font-bold leading-tight mt-5 mb-12 text-[#1a365d]">
            Capital → Assets → Cash Flow → Reinvestment → Compound permanently.
          </h2>
          <div className="space-y-2">
            {strategy.flywheel.map((step, i) => (
              <div key={i} className="flex items-center gap-4">
                <div className="w-8 h-8 rounded-full bg-[#e8f5f3] border border-[#2f9e8e]/40 flex items-center justify-center text-[#2f9e8e] text-xs font-bold flex-shrink-0">
                  {i + 1}
                </div>
                <div className="flex-1 py-3.5 px-5 bg-white border border-gray-200 rounded-lg text-gray-800">
                  {step}
                </div>
                {i < strategy.flywheel.length - 1 && <ArrowDown className="w-4 h-4 text-[#2f9e8e]/40 -mb-2 hidden md:block" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8 PLATFORMS */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div className="max-w-3xl">
              <Eyebrow>Eight Strategic Platforms</Eyebrow>
              <h2 className="font-serif text-3xl md:text-5xl font-bold leading-tight mt-5 text-[#1a365d]">
                One integrated operating system across the African opportunity.
              </h2>
            </div>
            <Link to="/strategy/platforms"
              className="inline-flex items-center gap-2 text-[#2f9e8e] hover:text-[#1a365d] font-medium"
              data-testid="home-link-platforms">
              Deep dive <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {strategy.platforms.map((p, i) => {
              const Icon = PLATFORM_ICONS[i] || CircleDot;
              return (
                <MotionReveal key={p.code} delay={i * 60}>
                  <div className="group h-full bg-white hover:bg-[#f6f9fb] border border-gray-200 hover:border-[#2f9e8e]/50 hover:shadow-lg rounded-xl p-6 transition-all hover:-translate-y-1">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-11 h-11 rounded-lg bg-[#e8f5f3] flex items-center justify-center group-hover:bg-[#2f9e8e]/20 transition-colors">
                        <Icon className="w-5 h-5 text-[#2f9e8e]" />
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-[#1a365d]">{p.target}</div>
                        <div className="text-[10px] uppercase tracking-wider text-gray-400">target</div>
                      </div>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#2f9e8e] font-bold mb-1">{p.code}</div>
                    <h3 className="font-serif text-xl font-bold mb-3 text-[#1a365d]">{p.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4">{p.tagline}</p>
                    <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Target return</div>
                    <div className="text-sm text-gray-800 font-medium">{p.returns}</div>
                  </div>
                </MotionReveal>
              );
            })}
          </div>

          <div className="mt-10 grid md:grid-cols-3 gap-4">
            {strategy.crossCutting.map((x) => (
              <div key={x.name} className="bg-gradient-to-br from-[#fcf5e3] to-white border border-[#c19a3c]/30 rounded-xl p-6">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#c19a3c] font-bold mb-2">Cross-cutting</div>
                <h3 className="font-serif text-lg font-bold text-[#1a365d] mb-2">{x.name}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{x.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CAPITAL ARCHITECTURE */}
      <section className="py-24 px-6 bg-[#f6f9fb]">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div className="max-w-3xl">
              <Eyebrow>Capital Architecture</Eyebrow>
              <h2 className="font-serif text-3xl md:text-5xl font-bold leading-tight mt-5 text-[#1a365d]">
                Where the $500B comes from. Where it goes.
              </h2>
            </div>
            <Link to="/strategy/capital"
              className="inline-flex items-center gap-2 text-[#2f9e8e] hover:text-[#1a365d] font-medium"
              data-testid="home-link-capital">
              Full breakdown <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid lg:grid-cols-2 gap-10">
            <MotionReveal>
              <div className="text-xs uppercase tracking-[0.2em] text-[#2f9e8e] font-bold mb-4">Sources</div>
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <CapitalBars
                  data={strategy.capitalSources.map((c) => ({ name: c.source, value: parseInt(String(c.amount).replace(/[^0-9]/g, ''), 10) }))}
                  color="#2f9e8e"
                  theme="light"
                  height={340}
                />
                <div className="flex items-center justify-between pt-4 mt-2 border-t border-gray-100">
                  <span className="text-sm font-semibold text-[#1a365d]">Total cumulative deployment</span>
                  <span className="text-xl font-bold text-[#2f9e8e]">$500B</span>
                </div>
              </div>
            </MotionReveal>
            <MotionReveal delay={120}>
              <div className="text-xs uppercase tracking-[0.2em] text-[#c19a3c] font-bold mb-4">Uses</div>
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <CapitalBars
                  data={strategy.capitalUses.map((c) => ({ name: c.use, value: parseInt(String(c.amount).replace(/[^0-9]/g, ''), 10) }))}
                  color="#c19a3c"
                  theme="light"
                  height={340}
                />
                <div className="flex items-center justify-between pt-4 mt-2 border-t border-gray-100">
                  <span className="text-sm font-semibold text-[#1a365d]">Total</span>
                  <span className="text-xl font-bold text-[#c19a3c]">$500B</span>
                </div>
              </div>
            </MotionReveal>
          </div>
        </div>
      </section>

      {/* ROADMAP */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div className="max-w-3xl">
              <Eyebrow>20-Year Roadmap · 2026 – 2046</Eyebrow>
              <h2 className="font-serif text-3xl md:text-5xl font-bold leading-tight mt-5 text-[#1a365d]">
                Five phases. Disciplined execution.
              </h2>
            </div>
            <Link to="/strategy/roadmap"
              className="inline-flex items-center gap-2 text-[#2f9e8e] hover:text-[#1a365d] font-medium"
              data-testid="home-link-roadmap">
              Phase KPIs <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <MotionReveal className="mb-10">
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-[0.2em] text-[#2f9e8e] font-bold">Cumulative capital deployment</div>
                <div className="text-xs text-gray-400">2026 → 2046</div>
              </div>
              <RoadmapArea
                data={[
                  { phase: 'Phase I',  value: 5 },
                  { phase: 'Phase II', value: 30 },
                  { phase: 'Phase III',value: 125 },
                  { phase: 'Phase IV', value: 275 },
                  { phase: 'Phase V',  value: 500 },
                ]}
                theme="light"
                height={260}
              />
            </div>
          </MotionReveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-3">
            {strategy.phases.map((p, i) => (
              <div key={p.label} className="bg-[#f6f9fb] border border-gray-200 rounded-xl p-5 hover:border-[#2f9e8e]/40 transition-colors">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#2f9e8e] font-bold">{p.label}</div>
                <h3 className="font-serif text-lg font-bold text-[#1a365d] mt-1 mb-1">{p.title}</h3>
                <div className="text-xs text-gray-500 mb-4">{p.years}</div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">Deployed</span>
                    <span className="font-bold text-[#1a365d]">{p.targets[0].v}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">Revenue</span>
                    <span className="font-bold text-[#1a365d]">{p.targets.find((t) => t.k === 'Annual revenue')?.v || '—'}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">NAV</span>
                    <span className="font-bold text-[#1a365d]">{p.targets.find((t) => t.k === 'Attributable NAV')?.v || '—'}</span>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-gray-200 text-xs text-gray-400">Phase {i + 1} of 5</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="py-24 px-6 bg-[#f6f9fb]" id="engage">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <Eyebrow>Engage</Eyebrow>
            <h2 className="font-serif text-3xl md:text-5xl font-bold leading-tight mt-5 mb-5 text-[#1a365d]">
              Three audiences. One platform. One conversation.
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Briefing packs are tailored to your audience and jurisdiction.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {Object.values(strategy.audiences).map((a) => (
              <div key={a.key} className="bg-white border border-gray-200 rounded-2xl p-7 hover:shadow-xl hover:border-[#2f9e8e]/40 transition-all hover:-translate-y-1">
                <div className="w-12 h-12 rounded-lg mb-5 flex items-center justify-center" style={{ background: `${a.color}22` }}>
                  <AudienceIcon a={a} />
                </div>
                <h3 className="font-serif text-2xl font-bold mb-3 text-[#1a365d]">{a.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-6">{a.pitch}</p>
                <button onClick={() => openBriefing(a.key)}
                  className="w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-lg font-semibold transition-all hover:-translate-y-0.5"
                  style={{ background: a.color, color: 'white' }}
                  data-testid={`closing-cta-${a.key}`}>
                  {a.cta} <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          <p className="text-center text-xs text-gray-500 mt-16 max-w-3xl mx-auto leading-relaxed">
            {strategy.notice}
          </p>
          <div className="text-center mt-6">
            <Link to="/strategy/teaser"
              className="inline-flex items-center gap-2 text-sm text-[#2f9e8e] hover:text-[#1a365d] transition-colors font-medium"
              data-testid="home-link-teaser">
              <FileText className="w-4 h-4" /> Download the 4-page executive briefing pack (PDF)
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      {audience && <BriefingRequestModal isOpen={!!audience} onClose={closeBriefing} audience={audience} />}
    </div>
  );
};

const AudienceIcon = ({ a }) => {
  const C = a.key === 'capital' ? Coins : a.key === 'sovereign' ? Landmark : Factory;
  return <C className="w-6 h-6" style={{ color: a.color }} />;
};

export default StrategyHome;
